import { useEffect, useState, useCallback } from "react";
import {
  Plus,
  KeyRound,
  Play,
  PowerOff,
  Trash2,
  Pencil,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ShieldAlert,
  Radio,
} from "lucide-react";
import PageHeader from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import AddSessionDialog from "../components/AddSessionDialog";
import { api, apiErrorMessage } from "../lib/api";
import { toast } from "sonner";
import { TID } from "../lib/testIds";

const STATE_META = {
  active: { label: "Activa", dot: "bg-emerald-500", text: "text-emerald-400" },
  closed: { label: "Cerrada", dot: "bg-zinc-500", text: "text-zinc-400" },
  error: { label: "Error", dot: "bg-red-500", text: "text-red-400" },
  unknown: { label: "Sin comprobar", dot: "bg-amber-500", text: "text-amber-400" },
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function SessionsPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/sessions");
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(apiErrorMessage(e));
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function openCreate() { setEditing(null); setDialogOpen(true); }
  function openEdit(s) { setEditing(s); setDialogOpen(true); }

  async function use(name, { checkOnly = false } = {}) {
    const action = checkOnly ? "check" : "use";
    setBusy((b) => ({ ...b, [name]: action }));
    try {
      const { data } = await api.post(`/sessions/${encodeURIComponent(name)}/use`, {
        force_reauth: false,
        check_only: checkOnly,
        timeout_ms: checkOnly ? 12000 : 25000,
      });
      if (checkOnly) {
        toast.success(data.logged_in ? "Conexión activa" : "Sin sesión activa");
      } else {
        const label = data.action === "reauthenticated" ? "reautenticada" : "reutilizada";
        toast.success(`Sesión ${label}`);
      }
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy((b) => { const c = { ...b }; delete c[name]; return c; });
    }
  }

  async function close(name) {
    if (!window.confirm(`¿Cerrar sesión "${name}"? Las credenciales se conservan.`)) return;
    setBusy((b) => ({ ...b, [name]: "close" }));
    try {
      await api.post(`/sessions/${encodeURIComponent(name)}/close`);
      toast.success("Sesión cerrada");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy((b) => { const c = { ...b }; delete c[name]; return c; });
    }
  }

  async function del(name) {
    if (!window.confirm(`¿Eliminar la sesión "${name}"? Se borrarán credenciales, script y storage_state.`)) return;
    setBusy((b) => ({ ...b, [name]: "delete" }));
    try {
      await api.delete(`/sessions/${encodeURIComponent(name)}`);
      toast.success("Sesión eliminada");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy((b) => { const c = { ...b }; delete c[name]; return c; });
    }
  }

  async function removeScript(name) {
    if (!window.confirm(
      `¿Eliminar el script legacy de "${name}"?\n\nEsta acción borra el archivo login.js del servidor. La sesión seguirá existiendo pero ya no podrá ejecutar el script.`
    )) return;
    setBusy((b) => ({ ...b, [name]: "remove-script" }));
    try {
      const { data } = await api.delete(`/sessions/${encodeURIComponent(name)}/script`);
      toast.success(data?.removed ? "Script legacy eliminado" : "No había script que eliminar");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy((b) => { const c = { ...b }; delete c[name]; return c; });
    }
  }

  return (
    <div data-testid={TID.sessions.root}>
      <PageHeader
        eyebrow="Automatización"
        title="Sesiones"
        description="Sesiones web con login automático. Las credenciales se cifran en KRAI API y solo se usan cuando abras o refresques la sesión."
        actions={
          <Button
            data-testid={TID.sessions.addButton}
            onClick={openCreate}
            className="bg-white text-black hover:bg-zinc-200 h-9"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Nueva sesión
          </Button>
        }
      />

      <div className="mb-6 rounded-md border border-amber-900/40 bg-amber-950/20 p-4 flex items-start gap-3 text-xs">
        <ShieldAlert className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
        <div className="text-amber-200/80 leading-relaxed">
          <div className="font-semibold text-amber-300 mb-0.5">
            No apto para Google / Microsoft / Meta / Apple
          </div>
          Estos proveedores bloquean logins automatizados con captcha y 2FA de
          dispositivo. Para Gemini y similares usa el módulo{" "}
          <span className="font-semibold">Navegador / Playwright</span> con
          storage_state.
        </div>
      </div>

      {loading ? (
        <div className="rounded-md border border-zinc-800 bg-zinc-950 p-10 text-sm text-zinc-500">
          Cargando sesiones…
        </div>
      ) : error ? (
        <div className="rounded-md border border-red-900/40 bg-red-950/20 text-red-300 text-xs p-3">
          {error}
        </div>
      ) : list.length === 0 ? (
        <div className="rounded-md border border-dashed border-zinc-800 bg-zinc-950 p-10 text-sm text-zinc-500">
          Todavía no hay sesiones. Crea la primera con el botón "Nueva sesión".
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((s) => (
            <SessionCard
              key={s.name}
              session={s}
              busy={busy[s.name]}
              onCheck={() => use(s.name, { checkOnly: true })}
              onUse={() => use(s.name, { checkOnly: false })}
              onClose={() => close(s.name)}
              onDelete={() => del(s.name)}
              onEdit={() => openEdit(s)}
              onRemoveScript={() => removeScript(s.name)}
            />
          ))}
        </div>
      )}

      <AddSessionDialog
        key={editing?.name || "new"}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        onSaved={load}
      />
    </div>
  );
}

function methodLabel(m) {
  if (m === "steps") return "Steps DSL";
  if (m === "script") return "Script (deprecado)";
  return "Plantilla";
}

function methodClass(m) {
  if (m === "script") return "text-amber-400";
  return "text-zinc-500";
}

function SessionCard({ session, busy, onCheck, onUse, onClose, onDelete, onEdit, onRemoveScript }) {
  const state = STATE_META[session.state] || STATE_META.unknown;
  const isLegacyScript = session.method === "script" || (session.script_deprecated && session.has_script);
  return (
    <div
      data-testid={TID.sessions.card(session.name)}
      className="rounded-md border border-zinc-800 bg-zinc-950 overflow-hidden hover:border-zinc-700 transition-colors duration-150 flex flex-col"
    >
      <div className="p-5 flex items-start justify-between gap-3">
        <div className="min-w-0 flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-md border border-zinc-800 bg-zinc-900 grid place-items-center">
            <KeyRound className="h-4 w-4 text-zinc-300" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white truncate">
              {session.name}
            </div>
            <div className={`text-[11px] font-mono uppercase tracking-wider ${methodClass(session.method)}`}>
              {methodLabel(session.method)}
            </div>
          </div>
        </div>
        <div className={`inline-flex items-center gap-1.5 text-[11px] ${state.text} shrink-0`}>
          <span className={`krai-dot ${state.dot}`} />
          {state.label}
        </div>
      </div>

      <div className="px-5 pb-4 space-y-3 flex-1">
        {isLegacyScript && (
          <div className="rounded-md border border-amber-900/50 bg-amber-950/20 p-2.5 flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-400 mt-0.5 shrink-0" />
            <div className="text-[11px] text-amber-200/90 leading-relaxed">
              <span className="font-semibold text-amber-300">method:"script" está deprecado.</span>{" "}
              La ejecución está deshabilitada por seguridad. Edita la sesión y migra a Plantilla o Steps DSL.
            </div>
          </div>
        )}
        <MetaRow label="URL">
          <a
            href={session.url}
            target="_blank"
            rel="noreferrer"
            className="font-mono text-xs text-zinc-300 hover:text-white truncate block"
          >
            {session.url}
          </a>
        </MetaRow>
        <div className="grid grid-cols-2 gap-3">
          <MetaRow label="Usuario">
            <span className="text-xs text-zinc-300 font-mono truncate block">
              {session.user_masked || <span className="text-zinc-600">—</span>}
            </span>
          </MetaRow>
          <MetaRow label="Credenciales">
            {session.has_credentials ? (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-400">
                <CheckCircle2 className="h-3 w-3" /> cifradas
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs text-amber-400">
                <AlertTriangle className="h-3 w-3" /> faltan
              </span>
            )}
          </MetaRow>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <MetaRow label="storage_state">
            {session.has_storage_state ? (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-400">
                <CheckCircle2 className="h-3 w-3" /> presente
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs text-zinc-500">
                <XCircle className="h-3 w-3" /> vacío
              </span>
            )}
          </MetaRow>
          <MetaRow label="Último uso">
            <span className="text-xs font-mono text-zinc-400">
              {formatDate(session.last_used_at)}
            </span>
          </MetaRow>
        </div>
      </div>

      <div className="mt-auto border-t border-zinc-800 p-3 flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="secondary"
          data-testid={TID.sessions.check(session.name)}
          onClick={onCheck}
          disabled={!!busy || !session.has_storage_state}
          className="h-8 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-200 disabled:opacity-40"
          title="Comprueba si la sesión sigue viva (no re-loguea)"
        >
          <Radio className="h-3.5 w-3.5 mr-1.5" />
          {busy === "check" ? "Comprobando…" : "Conexión"}
        </Button>
        <Button
          size="sm"
          data-testid={TID.sessions.use(session.name)}
          onClick={onUse}
          disabled={!!busy || !session.has_credentials}
          className="bg-white text-black hover:bg-zinc-200 h-8 disabled:opacity-40"
          title="Verifica y hace login automático si hace falta"
        >
          <Play className="h-3.5 w-3.5 mr-1.5" />
          {busy === "use" ? "Probando…" : "Probar"}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          data-testid={TID.sessions.close(session.name)}
          onClick={onClose}
          disabled={!!busy || !session.has_storage_state}
          className="h-8 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-200 disabled:opacity-40"
          title="Cierra la sesión (borra storage_state, mantiene credenciales)"
        >
          <PowerOff className="h-3.5 w-3.5 mr-1.5" />
          Cerrar
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={onEdit}
          disabled={!!busy}
          className="h-8 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-200"
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        {session.has_script && (
          <button
            data-testid={TID.sessions.removeScript(session.name)}
            onClick={onRemoveScript}
            disabled={!!busy}
            className="h-8 px-2 rounded-md text-[11px] text-amber-300 hover:text-amber-100 hover:bg-amber-950/40 border border-amber-900/40 transition-colors duration-150"
            title="Eliminar el script legacy del servidor (irreversible)"
          >
            Retirar script
          </button>
        )}
        <button
          data-testid={TID.sessions.delete(session.name)}
          onClick={onDelete}
          disabled={!!busy}
          className="ml-auto h-8 w-8 grid place-items-center rounded-md text-zinc-500 hover:text-red-400 hover:bg-zinc-900 transition-colors duration-150"
          title="Eliminar"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function MetaRow({ label, children }) {
  return (
    <div className="space-y-1 min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
        {label}
      </div>
      {children}
    </div>
  );
}
